/**
 * Browser client for the Simkl API.
 *
 * Confirmed at Gate A from a real Pages origin: Simkl permits cross-origin
 * reads and writes with an `Authorization` header and needs no `User-Agent`,
 * which browsers forbid setting anyway. No proxy is involved — the token never
 * leaves the device except to `api.simkl.com`.
 */
import { AuthExpiredError, NetworkError, RateLimitedError, SimklError } from "@/api/errors";
import {
  backoffMs,
  GET_PER_SECOND,
  POST_PER_SECOND,
  RateLimiter,
  systemClock,
} from "@/api/ratelimit";
import type { Clock } from "@/api/ratelimit";
import { parseLibrary, parseSearchResults } from "@/api/schemas";
import type { LibraryShow, SearchResult, ShowStatus } from "@/api/schemas";

const BASE = "https://api.simkl.com";
const MAX_ATTEMPTS = 5;

export interface ClientOptions {
  clientId: string;
  appName: string;
  appVersion: string;
  getToken: () => string | null;
  /** Called when Simkl rejects the token, so the UI can return to sign-in. */
  onAuthExpired?: () => void;
  fetchImpl?: typeof fetch;
  clock?: Clock;
  random?: () => number;
}

interface RequestOptions {
  method?: "GET" | "POST";
  query?: Record<string, string | number | undefined>;
  body?: unknown;
  signal?: AbortSignal;
}

export class SimklClient {
  readonly #options: ClientOptions;
  readonly #fetch: typeof fetch;
  readonly #clock: Clock;
  readonly #random: () => number;
  readonly #getLimiter: RateLimiter;
  readonly #postLimiter: RateLimiter;

  constructor(options: ClientOptions) {
    this.#options = options;
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#clock = options.clock ?? systemClock;
    this.#random = options.random ?? Math.random;
    this.#getLimiter = new RateLimiter(GET_PER_SECOND, this.#clock);
    this.#postLimiter = new RateLimiter(POST_PER_SECOND, this.#clock);
  }

  #url(path: string, query: RequestOptions["query"]): string {
    const url = new URL(path, BASE);
    url.searchParams.set("client_id", this.#options.clientId);
    url.searchParams.set("app-name", this.#options.appName);
    url.searchParams.set("app-version", this.#options.appVersion);
    for (const [key, value] of Object.entries(query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value));
    }
    return url.toString();
  }

  /** One attempt, through the appropriate rate limiter. */
  #send(path: string, method: "GET" | "POST", options: RequestOptions): Promise<Response> {
    const limiter = method === "GET" ? this.#getLimiter : this.#postLimiter;
    return limiter.run(async () => {
      const headers: Record<string, string> = { Accept: "application/json" };
      const token = this.#options.getToken();
      if (token !== null) headers["Authorization"] = `Bearer ${token}`;
      if (options.body !== undefined) headers["Content-Type"] = "application/json";

      const init: RequestInit = { method, headers };
      if (options.body !== undefined) init.body = JSON.stringify(options.body);
      if (options.signal !== undefined) init.signal = options.signal;

      try {
        return await this.#fetch(this.#url(path, options.query), init);
      } catch (cause) {
        if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
        throw new NetworkError(path, cause);
      }
    });
  }

  async #decode(response: Response, path: string): Promise<unknown> {
    const text = await response.text();
    if (text.length === 0) return null;
    try {
      return JSON.parse(text) as unknown;
    } catch (cause) {
      throw new NetworkError(path, cause);
    }
  }

  async request(path: string, options: RequestOptions = {}): Promise<unknown> {
    const method = options.method ?? "GET";

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
      const response = await this.#send(path, method, options);

      // A dead token never recovers: Simkl issues multi-year tokens with no
      // refresh endpoint, so retrying a 401 only delays the sign-in prompt.
      if (response.status === 401) {
        this.#options.onAuthExpired?.();
        throw new AuthExpiredError(path);
      }

      const retryable = response.status === 429 || response.status >= 500;
      if (retryable && attempt < MAX_ATTEMPTS - 1) {
        // No Retry-After to honour: CORS hides it (Gate A). Fixed backoff only.
        await this.#clock.sleep(backoffMs(attempt, this.#random));
        continue;
      }

      if (response.status === 429) throw new RateLimitedError(path);
      if (!response.ok) {
        throw new SimklError(`Simkl returned ${response.status}`, response.status, path);
      }

      return this.#decode(response, path);
    }

    throw new SimklError("Exhausted retries", 0, path);
  }

  /**
   * Every episode of one show, with air dates.
   *
   * The documented /calendar/*.json paths all 404; this is the working source
   * for air times, and its `date` is a full ISO instant with the network's UTC
   * offset already applied.
   */
  episodes(simklId: number, signal?: AbortSignal): Promise<unknown> {
    const path = `/tv/episodes/${simklId}`;
    const options: RequestOptions = { query: { extended: "full" } };
    if (signal !== undefined) options.signal = signal;
    return this.request(path, options);
  }

  /**
   * One show's own record, which is the only place its airing status lives.
   *
   * The library payload's `not_aired_episodes_count` was the obvious source
   * and is not usable: Simkl fills it only once episodes are scheduled, so it
   * reads 0 for 185 of 193 shows in a real library, and every show between
   * seasons looks finished. `status` here is "airing" or "ended" and says what
   * was actually meant.
   */
  show(simklId: number, signal?: AbortSignal): Promise<unknown> {
    const path = `/tv/${simklId}`;
    const options: RequestOptions = { query: { extended: "full" } };
    if (signal !== undefined) options.signal = signal;
    return this.request(path, options);
  }

  /** Title search. Shows only — movies and anime are out of scope (R25). */
  async search(query: string, signal?: AbortSignal): Promise<SearchResult[]> {
    const path = "/search/tv";
    const options: RequestOptions = { query: { q: query, extended: "full", limit: 20 } };
    if (signal !== undefined) options.signal = signal;
    return parseSearchResults(await this.request(path, options), path);
  }

  /** The user's shows for one list, with per-episode watched state. */
  async library(status: ShowStatus, signal?: AbortSignal): Promise<LibraryShow[]> {
    const path = `/sync/all-items/shows/${status}`;
    const options: RequestOptions = {
      query: { extended: "full", episode_watched_at: "yes" },
    };
    if (signal !== undefined) options.signal = signal;
    return parseLibrary(await this.request(path, options), path);
  }
}
